import {
  Component,
  Input,
  ViewChild,
  ViewContainerRef,
  TemplateRef,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  AfterViewInit,
  Optional,
  Output,
  EventEmitter,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { trigger, transition, style, animate } from '@angular/animations';
import { Subscription, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatMenuTrigger } from '@angular/material/menu';
import { ApiService } from 'src/app/services/api.service';
import { DataService } from 'src/app/services/data.service';
import { FilterService } from 'src/app/services/filter.service';
import { PageDataService } from 'src/app/services/page-data.service';
import {
  DataAggregationService,
  AggregationConfig,
} from 'src/app/services/data-aggregation.service';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface KpiTrend {
  value: any;
  direction: 'UP' | 'DOWN' | 'FLAT' | 'NONE';
  label: string;
}

export type VisualizationType = 'sparkline' | 'bar' | 'highlighted-bar' | 'category-bar';

export interface SparklineVisualization {
  type: 'sparkline';
  data: number[];
  labels?: any[]; // Optional labels for each data point (e.g. time periods)
}

export interface BarVisualization {
  type: 'bar';
  data: number[];
  labels?: string[]; // Optional category labels for each bar
}

export interface HighlightedBarVisualization {
  type: 'highlighted-bar';
  data: number[];
  selectedIndex: any;
  showDashedLine?: boolean;
  labels?: string[];
  isPrimary?: boolean[];
}

export interface CategoryBarVisualization {
  type: 'category-bar';
  label: string;
  data: number[];
  selectedIndex?: number;
  labels?: string[];
}

export type KpiVisualization =
  | SparklineVisualization
  | BarVisualization
  | HighlightedBarVisualization
  | CategoryBarVisualization;

export interface KpiCardConfig {
  title: string;
  mainValue: any;
  unit: string;
  chart_unit: string;
  trend: KpiTrend;
  visualization: KpiVisualization;
  tooltip?: string;
  previousValue?: number;
  changeAmount?: number;
  dateRange?: string;
  lastUpdated?: string;
  target?: number;
  chartColor?: string;
  fontSize?: string;
  fontWeight?: string;
  fontMainColor?: string;
  trendFlag?: boolean;
  trendLabel?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  showChart?: boolean;
  chartPosition?: 'right' | 'bottom';
  /** When true, reverses trend colors: UP = red, DOWN = green (e.g. for cost/error KPIs where lower is better). */
  reverseTrendColors?: boolean;
}

@Component({
  selector: 'app-kpi-chart',
  templateUrl: './kpi-chart.component.html',
  styleUrls: ['./kpi-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KpiCardChartComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  static key = 100821;

  @Input() kpiConfig!: KpiCardConfig;
  @Input('item') item: any;
  @Input('pageKey') pageKey: any;

  @ViewChild('svgContainer') svgContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('fullscreenTemplate') fullscreenTemplate!: TemplateRef<any>;
  @ViewChild(MatMenuTrigger) trigger!: MatMenuTrigger;
  @Output('onSizeChange') onSizeChange = new EventEmitter<any>()
  @ViewChild('kpiChartContainer', { static: true }) kpiChartContainer!: ElementRef
  // Overlay ref for fullscreen
  private overlayRef: OverlayRef | null = null;
  private fullscreenSvgContainerEl: HTMLDivElement | null = null;

  // Loader states
  isLoading = true;
  isFullscreen = false;
  infoData: any;
  isOpen = false;

  // Number of latest data points to show in sparkline/bar charts (configurable via item.config.data_points)
  dataPoints = 6;

  // Subscription management
  reqSubcription: Subscription[] = [];

  // ResizeObserver for responsive re-rendering
  private resizeObserver: ResizeObserver | null = null;
  private resizeDebounce: any = null;

  // Window resize subscription
  private windowResizeSub: Subscription | null = null;

  // Scroll listener that hides the D3 tooltip while the page scrolls
  private scrollHideHandler: (() => void) | null = null;

  // Chart constants — width/height are read dynamically from the container at render time
  private readonly MIN_CHART_WIDTH  = 80;
  private readonly MIN_CHART_HEIGHT = 50;
  private readonly MIN_BAR_GAP = 6;
  private readonly MAX_BAR_GAP = 12;
  private readonly SPARKLINE_PADDING = { top: 8, right: 8, bottom: 8, left: 8 };
  private readonly BAR_PADDING = { top: 8, right: 4, bottom: 8, left: 4 };

  /** Returns the live pixel dimensions of the SVG container, falling back to safe minimums. */
  private getChartSize(): { w: number; h: number } {
    const el = this.svgContainer?.nativeElement;
    const w = el ? Math.max(el.clientWidth  || el.offsetWidth,  this.MIN_CHART_WIDTH)  : 160;
    const h = el ? Math.max(el.clientHeight || el.offsetHeight, this.MIN_CHART_HEIGHT) : 100;
    return { w, h };
  }

  /** Returns live dimensions for fullscreen chart container with safe fallbacks. */
  private getFullscreenChartSize(): { w: number; h: number } {
    const el = this.fullscreenSvgContainerEl;
    const w = el ? Math.max(el.clientWidth || el.offsetWidth, this.MIN_CHART_WIDTH) : this.FULLSCREEN_CHART_WIDTH;
    const h = el ? Math.max(el.clientHeight || el.offsetHeight, this.MIN_CHART_HEIGHT) : this.FULLSCREEN_CHART_HEIGHT;
    return { w, h };
  }

  private getBarLayout(plotWidth: number, itemCount: number): { barWidth: number; gap: number } {
    if (itemCount <= 0) return { barWidth: 0, gap: this.MIN_BAR_GAP };
    const gap = Math.max(this.MIN_BAR_GAP, Math.min(this.MAX_BAR_GAP, Math.floor(plotWidth / Math.max(itemCount * 6, 1))));
    const barWidth = Math.max(4, (plotWidth - gap * (itemCount - 1)) / itemCount);
    return { barWidth, gap };
  }
  
  // Fullscreen chart constants
  private readonly FULLSCREEN_CHART_WIDTH = 600;
  private readonly FULLSCREEN_CHART_HEIGHT = 300;
  private readonly FULLSCREEN_SPARKLINE_PADDING = { top: 40, right: 40, bottom: 60, left: 65 };
  private readonly FULLSCREEN_BAR_PADDING = { top: 40, right: 40, bottom: 60, left: 65 };
  private readonly FULLSCREEN_MAX_BAR_WIDTH = 56;
  private readonly FULLSCREEN_MIN_BAR_GAP = 12;

  /** Bar layout for fullscreen — caps bar width and adds proportional gaps. */
  private getFullscreenBarLayout(plotWidth: number, itemCount: number): { barWidth: number; gap: number; offsetX: number } {
    if (itemCount <= 0) return { barWidth: 0, gap: this.FULLSCREEN_MIN_BAR_GAP, offsetX: 0 };

    // Start with the standard layout
    const rawBarWidth = (plotWidth - this.FULLSCREEN_MIN_BAR_GAP * (itemCount - 1)) / itemCount;
    const barWidth = Math.min(rawBarWidth, this.FULLSCREEN_MAX_BAR_WIDTH);
    const gap = Math.max(this.FULLSCREEN_MIN_BAR_GAP, Math.min(28, (plotWidth - barWidth * itemCount) / Math.max(itemCount - 1, 1)));
    const totalUsed = barWidth * itemCount + gap * (itemCount - 1);
    const offsetX = Math.max(0, (plotWidth - totalUsed) / 2);
    return { barWidth, gap, offsetX };
  }
  
  // Fullscreen resize subscription
  private fullscreenResizeSub: Subscription | null = null;

  constructor(
    private filterService: FilterService,
    private dataService: DataService,
    private apiService: ApiService,
    private httpClient: HttpClient,
    private cdr: ChangeDetectorRef,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    @Optional() private pageDataService: PageDataService,
    private dataAgg: DataAggregationService,
  ) {}

  ngOnInit(): void {
    this.setLoading(true);
    const cfg = this.item?.config;
    if (cfg) {
      this.dataPoints = Number(cfg['data_points']) || 6;
      this.buildConfigFromItem();
    }

    const dataMode = cfg?.['data_mode'] || 'widget'; // 'page' | 'widget'

    if (dataMode === 'widget') {
      // Widget mode: component fetches its own data independently
      this.loadApiData();
      this.reqSubcription.push(
        this.filterService.filterQuery.subscribe(() => {
          this.loadApiData();
        })
      );
    } else if (dataMode === 'page' && this.pageDataService) {
      let hasStartedLoading = false;

      // Page mode: data comes from centralized PageDataService
      this.reqSubcription.push(
        this.pageDataService.loading$.subscribe(loading => {
          if (loading) {
            hasStartedLoading = true;
            this.setLoading(true);
          }
        })
      );

      const kpiRaw = cfg?.['kpi_1_value_api'] || cfg?.['kpi_value_api'] || cfg?.['list_of_api'];
      const apiKey = typeof kpiRaw === 'string' || typeof kpiRaw === 'number'
        ? kpiRaw
        : kpiRaw?.api_key;
      const stream$ = apiKey
        ? this.pageDataService.getDataStream(String(apiKey))
        : this.pageDataService.getCommonDataStream();

      this.reqSubcription.push(
        stream$.subscribe(res => {
          if (res && res.length > 0 && cfg) {
            this.applyPageData(res, cfg);
            this.setLoading(false);
            this.cdr.detectChanges();
          } else if (hasStartedLoading) {
            this.setLoading(false);
            this.kpiConfig = { ...this.kpiConfig, mainValue: (cfg.integer_format.value != 'string' ? 0 : '') };
            this.cdr.detectChanges();
          }
        })
      );
    }
     const observer = new ResizeObserver((e: any) => {
      this.onSizeChange.emit(e[0].contentRect.height)
      observer.disconnect();
    });
    observer.observe(this.kpiChartContainer.nativeElement);
  }

  ngAfterViewInit(): void {
    if (this.kpiConfig) {
      this.renderVisualization();
    }
    // Re-render chart whenever the container is resized
    if (this.svgContainer?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.resizeDebounce);
        this.resizeDebounce = setTimeout(() => {
          // Clear cached SVG so createSvg() rebuilds it at the new size
          const existing = this.svgContainer?.nativeElement?.querySelector('svg');
          if (existing) existing.remove();
          this.renderVisualization();
        }, 100);
      });
      this.resizeObserver.observe(this.svgContainer.nativeElement);
    }

    // Also listen to window resize events to catch grid/panel layout changes
    this.windowResizeSub = fromEvent(window, 'resize')
      .pipe(debounceTime(150))
      .subscribe(() => {
        if (!this.kpiConfig) return;
        const existing = this.svgContainer?.nativeElement?.querySelector('svg');
        if (existing) existing.remove();
        this.renderVisualization();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['kpiConfig'] && !changes['kpiConfig'].firstChange) {
      this.renderVisualization();
    }
    if (changes['item'] && this.item?.config) {
      this.buildConfigFromItem();
      // this.loadApiData();
    }
  }

  ngOnDestroy(): void {
    this.reqSubcription.forEach((sub) => sub.unsubscribe());
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    clearTimeout(this.resizeDebounce);
    if (this.windowResizeSub) {
      this.windowResizeSub.unsubscribe();
      this.windowResizeSub = null;
    }
    if (this.fullscreenResizeSub) {
      this.fullscreenResizeSub.unsubscribe();
      this.fullscreenResizeSub = null;
    }
    // Remove the scroll listener and hide any lingering tooltip
    if (this.scrollHideHandler) {
      window.removeEventListener('scroll', this.scrollHideHandler, { capture: true });
      this.scrollHideHandler = null;
    }
    const tooltip = document.querySelector('.d3-chart-tooltip') as HTMLElement | null;
    if (tooltip) tooltip.style.opacity = '0';
  }

  // ─── API Data Loading (card2kpi pattern) ──────────────────────────────────

  /**
   * Build KpiCardConfig from the generic item.config coming from the dashboard framework
   */
  private buildConfigFromItem(): void {
    const cfg = this.item?.config;
    if (!cfg) return;

    // Derive visualization type from chart_type object or fallback
    const chartType = cfg['chart_type']?.value || cfg['chart_type']?.name || this.kpiConfig?.visualization?.type || 'sparkline';
    const chartColor = cfg['chart_color'] || this.kpiConfig?.chartColor;

    this.kpiConfig = {
      title: cfg['title'] || cfg['kpi_heading'] || cfg['kpi_1_heading'] || this.kpiConfig?.title || '',
      mainValue: cfg['mainValue'] ?? this.kpiConfig?.mainValue ?? (cfg.integer_format.value == 'string' ? '' : 0),
      unit: cfg['unit'] || cfg['unit_label'] || cfg['unit_1_label'] || '',
      chart_unit: cfg['chart_unit_label'] || '',
      trend: cfg['trend'] || this.kpiConfig?.trend || { value: 0, direction: 'FLAT', label: '' },
      visualization: cfg['visualization'] || this.kpiConfig?.visualization || { type: chartType, data: [] },
      tooltip: cfg['tooltip'] || cfg['info_text'] || '',
      previousValue: cfg['previousValue'],
      changeAmount: cfg['changeAmount'],
      dateRange: cfg['dateRange'],
      lastUpdated: cfg['lastUpdated'],
      target: cfg['target'],
      chartColor: chartColor,
      fontSize: cfg['font_size'],
      fontWeight: cfg['font_weight'],
      fontMainColor: cfg['main_color'],
      trendFlag: cfg['trend_flag'],
      trendLabel: cfg['trend_label'],
      xAxisLabel:cfg['xAxisLabel'] || 'Period',
      yAxisLabel:cfg['yAxisLabel'] || 'Patients',
      // Designer-driven layout controls
      showChart: cfg['is_Chart'] !== undefined
        ? !!cfg['is_Chart']
        : (this.kpiConfig?.showChart !== undefined ? this.kpiConfig.showChart : true),
      chartPosition: (cfg['chart_position'] === 'bottom' || cfg['chart_position'] === 'right')
        ? cfg['chart_position']
        : (this.kpiConfig?.chartPosition || 'right'),
      reverseTrendColors: cfg['reverse_trend_colors'] 
    };
    this.cdr.markForCheck();
  }

  /**
   * Load data from API based on item.config (follows card1kpi pattern)
   */
  loadApiData(): void {
    const cfg = this.item?.config;
    this.setLoading(true);

       // Support kpi_1_value_api as plain string (api_key directly) or object with api_key property
    const kpi1Raw = cfg['kpi_1_value_api'] || cfg['kpi_value_api'];
    const apiKey = typeof kpi1Raw === 'string' || typeof kpi1Raw === 'number'
      ? kpi1Raw
      : kpi1Raw?.api_key;
// console.log('KPI Chart API Key', apiKey);
    if (!apiKey || this.isEmptyObject(this.filterService.baseQuery)) {
      return;
    }

    const query = Object.assign({}, this.filterService.baseQuery, { api_key: apiKey });
    query['report_typ'] = this.filterService.report_type;
    query['module_key'] = this.apiService.getModuleKey();

    const widgetEndpoint = cfg['widget_endpoint'];
    const url = `${this.apiService.apiBaseUrl}/${widgetEndpoint ? widgetEndpoint : this.apiService.executionUrl}`;

    this.reqSubcription.push(
      this.httpClient.post(url, query).subscribe(
        (res: any) => {
          if (res && res.length > 0) {
            this.applyApiResponse(res, cfg);
          } else {
            this.kpiConfig = { ...this.kpiConfig, mainValue: 0 };
          }
          this.setLoading(false);
          this.cdr.detectChanges();
        },
        (err: any) => {
          console.error('KPI Chart API error', err);
          this.setLoading(false);
          this.kpiConfig = { ...this.kpiConfig, mainValue: 0 };
          this.cdr.detectChanges();
        }
      )
    );
  }

  private setLoading(state: boolean): void {
    this.isLoading = state;
    this.cdr.markForCheck();
  }

  /**
   * Apply API response data to build full config including visualization
   */
  private applyApiResponse(res: any[], cfg: any): void {
    const row = res[0];
    // Resolve the value column name from cfg
    const colRaw = cfg['kpi_1_value_api_col'] || cfg['kpi_value_api_col'];
    const colName = typeof colRaw === 'string' ? colRaw : colRaw?.['api_resp_column_nm'];

    // Derive chart type from cfg
    const chartType = cfg['chart_type']?.value || cfg['chart_type']?.name || this.kpiConfig?.visualization?.type || 'sparkline';

    // Extract mainValue from the column (e.g. row["total_patients"] = 3581 or "45%")
    let mainValue:any = this.kpiConfig.mainValue;
    let unit = this.kpiConfig.unit;
    if (colName && row[colName] !== undefined) {
      const rawVal = row[colName];
      if (typeof rawVal === 'string' && rawVal.includes('%')) {
        mainValue = parseFloat(rawVal.replace(/[^0-9.\-]/g, '')) || 0;
        unit = '%';
      } else if (typeof rawVal === 'string') {
        if(cfg.integer_format.value == 'string') mainValue = rawVal;
        else {
          // Handle other suffixes like "$100", "1.2K", etc.
          const numericPart = parseFloat(rawVal?.replace(/[^0-9.\-]/g, ''));
          const suffix = rawVal.replace(/[0-9.\-\s]/g, '').trim();
          mainValue = numericPart || 0;
          if (suffix) unit = suffix;
        }
      } else {
        // mainValue = Number(rawVal) || 0;
        mainValue = cfg.integer_format.value != 'string' ? (Number(rawVal) || 0) : ''
      }
    }

    // Extract trend from response (e.g. row["trend"] = "DOWN", row["trend_value"] = 52)
    const trendDirection = row['trend'] ;
    const trendValue = row['trend_value'] ?? null
    const trend: KpiTrend = trendDirection
      ? {
          value: trendValue ?? null,
          direction: String(trendDirection).toUpperCase() as 'UP' | 'DOWN' | 'FLAT'|'NONE',
          label: row['trend_label'] || 'vs last month',
        }
      : this.kpiConfig.trend;

    // Parse chartdata: supports comma-separated string "64,48,72" or actual array
    const chartDataRaw = row['chartdata'] ?? row['chart_data'] ?? row['viz_data'];
    const vizData = this.parseCommaSeparatedNumbers(chartDataRaw);

    // Parse labels: supports quoted comma-separated string "'Sep', 'Oct'" or actual array
    const labelsRaw = row['labels'] ?? row['chart_labels'] ?? row['viz_labels'];
    const vizLabels = this.parseCommaSeparatedStrings(labelsRaw);

    this.kpiConfig = {
      ...this.kpiConfig,
      mainValue,
      unit,
      trend,
      visualization: {
        ...this.kpiConfig.visualization,
        type: chartType,
        data: vizData.length > 0 ? vizData : (this.kpiConfig.visualization as any).data || [],
        ...(vizLabels.length > 0 ? { labels: vizLabels } : {}),
      } as any,
    };

    this.renderVisualization();
  }

  /**
   * Aggregate raw rows into a single summary row + sparkline/trend fields
   * using the declarative `item.config.aggregation` block. If the designer
   * has not supplied an aggregation config, we fall back to a default that
   * reproduces the legacy hardcoded behavior for BI KPI mock data.
   */
  /**
   * Handle page-mode data: apply aggregation if AggregationEnabled, otherwise use as-is.
   */
  private applyPageData(res: any[], cfg: any): void {
    if (cfg?.['AggregationEnabled'] === true) {
      const aggregated = this.aggregateCommonData(res);
      this.applyApiResponse([aggregated.summary], cfg);
    } else {
      this.applyApiResponse(res, cfg);
    }
  }

  private aggregateCommonData(rows: any[]): { summary: any } {
    const cfg = this.item?.config || {};
    const aggCfg: AggregationConfig =
      (cfg['aggregation'] as AggregationConfig) || this.buildDefaultAggregationConfig(cfg);

    const result = this.dataAgg.aggregate(rows, aggCfg);

    // Resolve the metric this card displays so we can pull sparkline + trend
    // for it specifically. Prefer the config-declared trackField; otherwise
    // derive from the existing kpi_1_value_api_col prop.
    const trackField =
      aggCfg.timeSeries?.trackField ||
      this.resolveColName(cfg) ||
      Object.keys(aggCfg.fields || {})[0];

    const tsOpts = {
      ...(aggCfg.timeSeries || {}),
      trackField,
    };
    const series = this.dataAgg.buildTimeSeries(result, tsOpts);
    const trend = this.dataAgg.computeTrend(series.values);

    const summary: any = {
      ...result.summary,
      chartdata: series.values.join(','),
      labels: series.labels.join(','),
      trend: trend.direction,
      trend_value: trend.value,
      trend_label: 'since last month',
    };

    return { summary };
  }

  /** Resolves the raw column name the KPI card is tracking from item.config. */
  private resolveColName(cfg: any): string {
    const colRaw = cfg?.['kpi_1_value_api_col'] || cfg?.['kpi_value_api_col'];
    return typeof colRaw === 'string' ? colRaw : colRaw?.['api_resp_column_nm'] || '';
  }

  /**
   * Back-compat shim: when the designer has not supplied `item.config.aggregation`,
   * produce a config that matches the old hardcoded BI KPI behavior so existing
   * dashboards render identically.
   */
  private buildDefaultAggregationConfig(cfg: any): AggregationConfig {
    debugger
    return {
      groupBy: cfg['aggregation_groupBy'] || 'period',
      fields:cfg['aggregation_fields'] || {
        benefit_covered:     { source: 'benefit_covered',     op: 'sum' },
        total_bi_requests:   { source: 'total_bi_requests',   op: 'sum' },
        bi_tat_less_than_60: { source: 'bi_tat_less_than_60', op: 'sum' },
        total_patients:      { source: 'total_patients',      op: 'sum' },
        new_patients:        { source: 'new_patients',        op: 'sum' },
        total_pa_tat:        { source: 'total_pa_tat',        op: 'sum' },
        total_pa_tat_vol:    { source: 'total_pa_tat_vol',    op: 'sum' },
        pa_volume:           { source: 'pa_volume',           op: 'sum' },
        total_pa_required:   { source: 'total_pa_required',   op: 'sum' },
        pct_pa_request :     { source: 'pct_pa_request',      op: 'sum' },

      },
      computed: cfg['aggregation_computed'] || {
        bi_tat_pct: {
          op: 'percent',
          numerator: 'bi_tat_less_than_60',
          denominator: 'total_bi_requests',
          round: 2,
          suffix: this.resolveColName(cfg) === 'bi_tat_pct' ? '%' : undefined,
        },
        bi_covered_pct: {
          op: 'percent',
          numerator: 'benefit_covered',
          denominator: 'total_bi_requests',
          round: 2,
          suffix: this.resolveColName(cfg) === 'bi_covered_pct' ? '%' : undefined,
        },
        avg_bus_hours: {
          op: 'ratio',
          numerator: 'total_pa_tat',
          denominator: 'total_pa_tat_vol',
          round: 2,
          suffix: this.resolveColName(cfg) === 'pct_pa_request' ? 'h' : undefined,  
        },
        pct_pa_request: {
          op: 'percent',
          numerator: 'total_pa_required',
          denominator: 'pa_volume',
          round: 2,
          suffix: this.resolveColName(cfg) === 'pct_pa_request' ? '%' : undefined,  
        },
      },
      timeSeries: cfg['aggregation_timeSeries'] ||{
        orderBy: 'period',
        limit: this.dataPoints,
        labelFormat: 'month',
        trackField: this.resolveColName(cfg) || 'total_patients',
      },
    };
  }

  /**
   * Parse a comma-separated number string "64,48,72" or an actual array into number[]
   */
  private parseCommaSeparatedNumbers(raw: any): number[] {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map((v: any) => Number(v) || 0);
    if (typeof raw === 'string') {
      return raw.split(',').map((s: string) => Number(s.trim()) || 0).filter((n: number) => !isNaN(n));
    }
    return [];
  }

  /**
   * Parse a comma-separated label string "'Sep', 'Oct', 'Nov'" or an actual array into string[]
   */
  private parseCommaSeparatedStrings(raw: any): string[] {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map((v: any) => String(v));
    if (typeof raw === 'string') {
      return raw.split(',').map((s: string) => s.trim().replace(/^['"]|['"]$/g, ''));
    }
    return [];
  }

  /**
   * Fetch widget info detail (card2kpi pattern)
   */
  getInfoDetails(): boolean {

    let widgetUniqKey = (this.item?.widget_uniq_key || this.item?.key || '').replace(/[^a-zA-Z0-9]/g, '');
    const data = {
      api_key: this.item?.config?.['kpi_1_value_api'] || this.item?.config?.['kpi_1_value_api']?.api_key || 0,
      widget_key: (this.pageDataService && this.pageDataService?.isActive) ? widgetUniqKey : this.item?.config?.['widgetKey'] + (this.item?.config?.['info_key']|| ""),
      page_key: this.pageKey,
      report_typ: 'D',
      brand_key: this.filterService.baseQuery?.brand_key,
    };
    this.reqSubcription.push(
      this.dataService.getInfoDetail(data).subscribe((res: any) => {
        this.infoData = res?.[0] || { info_header: 'No Info Data Found' };
        this.cdr.markForCheck();
      })
    );
    return (this.isOpen = !this.isOpen);
  }

  private isEmptyObject(obj: any): boolean {
    return !obj || Object.keys(obj).length === 0;
  }

  /**
   * Main method to render visualization based on type
   */
  private renderVisualization(): void {
    setTimeout(() => {
      const { type } = this.kpiConfig.visualization;

      switch (type) {
        case 'sparkline':
          this.renderSparkline();
          break;
        case 'bar':
          this.renderBarChart();
          break;
        case 'highlighted-bar':
          this.renderHighlightedBarChart();
          break;
        case 'category-bar':
          this.renderCategoryBarChart();
          break;
      }
    });
  }

  /**
   * Render sparkline chart (smooth curve with SVG glow effect)
   * Requirement 2: Smooth sparkline using d3.curveCatmullRom with SVG drop shadow filter
   */
  private renderSparkline(): void {
    const viz = this.kpiConfig.visualization as Extract<
      KpiVisualization,
      { type: 'sparkline' }
    >;
    const data = viz.data;

    if (!data || data.length === 0) return;

    const { w: chartW, h: chartH } = this.getChartSize();
    const padding = this.SPARKLINE_PADDING;
    const width  = chartW - padding.left - padding.right;
    const height = chartH - padding.top  - padding.bottom;

    const minValue = Math.min(...data);
    const maxValue = Math.max(...data);
    const range = maxValue - minValue || 1;

    // Create SVG
    const svg = this.createSvg();
    this.clearSvg(svg);

    // Add SVG drop shadow filter for glow effect
    this.createDropShadowFilter(svg, 'sparklineGlow');

    // Create smooth curve path using Catmull-Rom interpolation
    const points = data.map((d, i) => {
      const x = padding.left + (width / (data.length - 1)) * i;
      const y = padding.top + height - ((d - minValue) / range) * height;
      return { x, y, value: d, index: i };
    });

    const pathData = this.generateCatmullRomPath(points);

    // Add gradient
    const gradient = this.createGradient(svg, 'sparklineGradient');

    // Add fill area with gradient
    // const areaPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    // const closingPath = `${pathData} L ${padding.left + width} ${padding.top + height} L ${padding.left} ${padding.top + height} Z`;
    // areaPath.setAttribute('d', closingPath);
    // areaPath.setAttribute('fill', 'url(#sparklineGradient)');
    // areaPath.setAttribute('opacity', '0.15');
    // svg.appendChild(areaPath);

    // Add x-axis baseline
    const xAxisY = padding.top + height;
    const xAxisLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    xAxisLine.setAttribute('x1', String(padding.left));
    xAxisLine.setAttribute('y1', String(xAxisY));
    xAxisLine.setAttribute('x2', String(padding.left + width));
    xAxisLine.setAttribute('y2', String(xAxisY));
    xAxisLine.setAttribute('stroke', '#d1d5db');
    xAxisLine.setAttribute('stroke-width', '0');
    svg.appendChild(xAxisLine);

    // Add tick marks at each data point along the x-axis
    points.forEach(pt => {
      const tick = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      tick.setAttribute('x1', String(pt.x));
      tick.setAttribute('y1', String(xAxisY));
      tick.setAttribute('x2', String(pt.x));
      tick.setAttribute('y2', String(xAxisY + 4));
      tick.setAttribute('stroke', '#d1d5db');
      tick.setAttribute('stroke-width', '1');
      svg.appendChild(tick);
    });

    // Add smooth line with glow effect
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', pathData);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', '#2979FF');
    path.setAttribute('stroke-width', '3');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('filter', 'url(#sparklineGlow)');
    svg.appendChild(path);

    // Add interactive D3-style tooltip overlay
    this.addSparklineD3Tooltip(svg, points, padding, width, height, minValue, maxValue, chartW, viz.labels);
  }

  /**
   * Render basic bar chart
   */
  private renderBarChart(): void {
    const viz = this.kpiConfig.visualization as Extract<
      KpiVisualization,
      { type: 'bar' }
    >;
    const data = viz.data;

    if (!data || data.length === 0) return;

    this.renderBars(data, -1);
  }

  /**
   * Render highlighted bar chart with isPrimary logic
   * Requirement 3: Magenta (#D500F9) for primary bars, light grey for others
   */
  private renderHighlightedBarChart(): void {
    const viz = this.kpiConfig.visualization as Extract<
      KpiVisualization,
      { type: 'highlighted-bar' }
    >;
    const data = viz.data;
    const selectedIndex = viz.selectedIndex;
    const isPrimary = viz.isPrimary || [];

    if (!data || data.length === 0) return;

    const { w: chartW, h: chartH } = this.getChartSize();
    const padding = this.BAR_PADDING;
    const width  = chartW - padding.left - padding.right;
    const height = chartH - padding.top  - padding.bottom;

    const maxValue = Math.max(...data);
    const { barWidth, gap } = this.getBarLayout(width, data.length);

    const svg = this.createSvg();
    this.clearSvg(svg);

    const hbLabels = viz.labels || data.map((_, i) => `Item ${i + 1}`);
    const tooltipDiv = this.getOrCreateD3TooltipContainer();

    // Render bars with isPrimary logic + D3 tooltip
    data.forEach((value, index) => {
      const barHeight = (value / maxValue) * height;
      const x = padding.left + index * (barWidth + gap);
      const y = padding.top + height - barHeight;
      const barCenterX = x + barWidth / 2;

      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(x));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(barWidth));
      rect.setAttribute('height', String(barHeight));
      const defaultFill = isPrimary[index] ? '#2979FF' : '#CFD8DC';
      rect.setAttribute('fill', defaultFill);
      rect.setAttribute('rx', '4');
      rect.setAttribute('ry', '4');
      rect.style.cursor = 'pointer';
      rect.style.transition = 'fill 0.2s ease';
      svg.appendChild(rect);

      // Transparent hover zone covering full bar column
      const hoverRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      hoverRect.setAttribute('x', String(x - 1));
      hoverRect.setAttribute('y', String(padding.top));
      hoverRect.setAttribute('width', String(barWidth + gap));
      hoverRect.setAttribute('height', String(height));
      hoverRect.setAttribute('fill', 'transparent');
      hoverRect.style.cursor = 'pointer';

      hoverRect.addEventListener('mouseenter', (event: MouseEvent) => {
        rect.setAttribute('fill', isPrimary[index] ? '#aa00d4' : '#2979FF');
        this.showUnifiedTooltip(tooltipDiv, event.clientX + 15, event.clientY - 60, hbLabels[index], value, defaultFill);
      });

      hoverRect.addEventListener('mousemove', (event: MouseEvent) => {
        tooltipDiv.style.left = `${event.clientX + 15}px`;
        tooltipDiv.style.top  = `${event.clientY - 60}px`;
      });

      hoverRect.addEventListener('mouseleave', () => {
        rect.setAttribute('fill', defaultFill);
        tooltipDiv.style.opacity = '0';
      });

      svg.appendChild(hoverRect);
    });
  }

  /**
   * Render category bar chart with label
   */
  private renderCategoryBarChart(): void {
    const viz = this.kpiConfig.visualization as Extract<
      KpiVisualization,
      { type: 'category-bar' }
    >;
    const data = viz.data;
    const selectedIndex = viz.selectedIndex ?? 0;

    if (!data || data.length === 0) return;

    this.renderBars(data, selectedIndex);
  }

  /**
   * Helper method to render interactive bar chart
   * Requirement 1: Interactive bars with hover, vertical guide line, and high-fidelity tooltip
   */
  private renderBars(data: number[], selectedIndex: number = -1): void {
    const { w: chartW, h: chartH } = this.getChartSize();
    const padding = this.BAR_PADDING;
    const width  = chartW - padding.left - padding.right;
    const height = chartH - padding.top  - padding.bottom;

    const maxValue = Math.max(...data, 1);
    const { barWidth, gap } = this.getBarLayout(width, data.length);

    const svg = this.createSvg();
    this.clearSvg(svg);

    // Get labels from config if available (for bar, highlighted-bar and category-bar)
    const viz = this.kpiConfig.visualization;
    const labels = (viz.type === 'bar' && viz.labels) ? viz.labels :
                   (viz.type === 'highlighted-bar' && viz.labels) ? viz.labels :
                   (viz.type === 'category-bar' && viz.labels) ? viz.labels :
                   data.map((_, i) => `Item ${i + 1}`);

    const tooltipDiv = this.getOrCreateD3TooltipContainer();

    // Create vertical guide line (hidden by default)
    const guideLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    guideLine.setAttribute('stroke', '#2979FF');
    guideLine.setAttribute('stroke-width', '1.5');
    guideLine.setAttribute('stroke-dasharray', '4,3');
    guideLine.setAttribute('opacity', '0');
    guideLine.style.transition = 'opacity 0.05s ease';
    svg.appendChild(guideLine);

    data.forEach((value, index) => {
      const barHeight = (value / maxValue) * height;
      const x = padding.left + index * (barWidth + gap);
      const y = padding.top + height - barHeight;
      const barCenterX = x + barWidth / 2;

      // Create visible bar
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(x));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(barWidth));
      rect.setAttribute('height', String(barHeight));
      rect.setAttribute('rx', '4');
      rect.setAttribute('ry', '4');
      rect.style.cursor = 'pointer';
      rect.style.transition = 'fill 0.2s ease';

      // Default color: muted light grey-blue
      let defaultColor = '#CFD8DC';
      if (selectedIndex >= 0 && index === selectedIndex) {
        defaultColor = '#CFD8DC';
      } else if (selectedIndex < 0) {
        defaultColor = '#CFD8DC';
      }
      rect.setAttribute('fill', defaultColor);

      // Create transparent overlay rect for better hover detection (entire bar band)
      const hoverRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      hoverRect.setAttribute('x', String(x - 1));
      hoverRect.setAttribute('y', String(padding.top));
      hoverRect.setAttribute('width', String(barWidth + gap));
      hoverRect.setAttribute('height', String(height));
      hoverRect.setAttribute('fill', 'transparent');
      hoverRect.style.cursor = 'pointer';

      hoverRect.addEventListener('mouseenter', (event: MouseEvent) => {
        rect.setAttribute('fill', '#2979FF');
        guideLine.setAttribute('x1', String(barCenterX));
        guideLine.setAttribute('y1', String(padding.top));
        guideLine.setAttribute('x2', String(barCenterX));
        guideLine.setAttribute('y2', String(chartH));
        guideLine.setAttribute('opacity', '1');
        this.showUnifiedTooltip(tooltipDiv, event.clientX + 15, event.clientY - 60, labels[index], value, '#2979FF');
      });

      hoverRect.addEventListener('mousemove', (event: MouseEvent) => {
        tooltipDiv.style.left = `${event.clientX + 15}px`;
        tooltipDiv.style.top  = `${event.clientY - 60}px`;
      });

      hoverRect.addEventListener('mouseleave', () => {
        rect.setAttribute('fill', defaultColor);
        guideLine.setAttribute('opacity', '0');
        tooltipDiv.style.opacity = '0';
      });

      svg.appendChild(rect);
      svg.appendChild(hoverRect);
    });
  }

  /**
   * Create or get SVG element
   */
  private createSvg(): SVGSVGElement {
    const { w, h } = this.getChartSize();
    let svg = this.svgContainer?.nativeElement?.querySelector('svg');

    if (!svg) {
      svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');

      if (this.svgContainer?.nativeElement) {
        this.svgContainer.nativeElement.appendChild(svg);
      }
    }

    // Always sync viewBox to the current container size so the coordinate
    // space matches the allocated pixels exactly on every render/resize.
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);

    return svg;
  }

  /**
   * Clear all children from SVG
   */
  private clearSvg(svg: SVGSVGElement): void {
    while (svg.lastChild) {
      svg.removeChild(svg.lastChild);
    }
  }

  /**
   * Create gradient definition in SVG
   */
  private createGradient(
    svg: SVGSVGElement,
    gradientId: string
  ): SVGGradientElement {
    const defs = svg.querySelector('defs') || this.createDefs(svg);
    let gradient = defs.querySelector(`#${gradientId}`);

    if (!gradient) {
      gradient = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'linearGradient'
      );
      gradient.setAttribute('id', gradientId);
      gradient.setAttribute('x1', '0%');
      gradient.setAttribute('y1', '0%');
      gradient.setAttribute('x2', '100%');
      gradient.setAttribute('y2', '0%');

      const stop1 = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'stop'
      );
      stop1.setAttribute('offset', '0%');
      stop1.setAttribute('stop-color', '#3B82F6');
      stop1.setAttribute('stop-opacity', '1');

      const stop2 = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'stop'
      );
      stop2.setAttribute('offset', '100%');
      stop2.setAttribute('stop-color', '#60A5FA');
      stop2.setAttribute('stop-opacity', '1');

      (gradient as any).appendChild(stop1);
      (gradient as any).appendChild(stop2);
      defs.appendChild(gradient);
    }

    return gradient as SVGGradientElement;
  }

  /**
   * Create defs element in SVG
   */
  private createDefs(svg: SVGSVGElement): SVGDefsElement {
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    svg.insertBefore(defs, svg.firstChild);
    return defs;
  }

  /**
   * Get trend color class
   */
  getTrendColorClass(): string {
    const dir = this.kpiConfig.trend.direction;
    const reverse = !!this.kpiConfig.reverseTrendColors;
    if (dir === 'UP')   return reverse ? 'trend-down' : 'trend-up';
    if (dir === 'DOWN') return reverse ? 'trend-up'   : 'trend-down';
    return 'trend-flat';
  }

  /**
   * Get trend icon
   */
  getTrendIcon(): string {
    const dir = this.kpiConfig.trend.direction;
    if (dir === 'UP')   return '▲';
    if (dir === 'DOWN') return '▼';
    return '';
  }

  /**
   * Get category label for category-bar visualization
   */
  getCategoryLabel(): string {
    const viz = this.kpiConfig.visualization;
    if (viz.type === 'category-bar') {
      return viz.label;
    }
    return '';
  }

  /**
   * Generate smooth Catmull-Rom curve path
   * Used for sparkline smooth interpolation
   */
  private generateCatmullRomPath(points: Array<{x: number, y: number}>): string {
    if (points.length < 2) return '';
    if (points.length === 2) {
      return `M ${points[0].x},${points[0].y} L ${points[1].x},${points[1].y}`;
    }

    const tension = 0.5; // Catmull-Rom tension parameter
    let path = `M ${points[0].x},${points[0].y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(points.length - 1, i + 2)];

      // Catmull-Rom to Bezier conversion
      const cp1x = p1.x + (p2.x - p0.x) / 6 * tension;
      const cp1y = p1.y + (p2.y - p0.y) / 6 * tension;
      const cp2x = p2.x - (p3.x - p1.x) / 6 * tension;
      const cp2y = p2.y - (p3.y - p1.y) / 6 * tension;

      path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
    }

    return path;
  }

  /**
   * Create SVG drop shadow filter for glow effect
   */
  private createDropShadowFilter(svg: SVGSVGElement, filterId: string): void {
    const defs = svg.querySelector('defs') || this.createDefs(svg);
    
    if (defs.querySelector(`#${filterId}`)) return;

    const filter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
    filter.setAttribute('id', filterId);
    filter.setAttribute('x', '-50%');
    filter.setAttribute('y', '-50%');
    filter.setAttribute('width', '200%');
    filter.setAttribute('height', '200%');

    // Gaussian blur for glow
    const feGaussianBlur = document.createElementNS('http://www.w3.org/2000/svg', 'feGaussianBlur');
    feGaussianBlur.setAttribute('in', 'SourceGraphic');
    feGaussianBlur.setAttribute('stdDeviation', '2');
    feGaussianBlur.setAttribute('result', 'blur');

    // Offset for drop shadow
    const feOffset = document.createElementNS('http://www.w3.org/2000/svg', 'feOffset');
    feOffset.setAttribute('in', 'blur');
    feOffset.setAttribute('dx', '0');
    feOffset.setAttribute('dy', '1');
    feOffset.setAttribute('result', 'offsetBlur');

    // Merge with original
    const feMerge = document.createElementNS('http://www.w3.org/2000/svg', 'feMerge');
    
    const feMergeNode1 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
    feMergeNode1.setAttribute('in', 'offsetBlur');
    
    const feMergeNode2 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
    feMergeNode2.setAttribute('in', 'SourceGraphic');

    feMerge.appendChild(feMergeNode1);
    feMerge.appendChild(feMergeNode2);

    filter.appendChild(feGaussianBlur);
    filter.appendChild(feOffset);
    filter.appendChild(feMerge);

    defs.appendChild(filter);
  }

  /**
   * Toggle fullscreen mode using CDK Overlay
   */
  toggleFullscreen(): void {
    if (this.overlayRef) return; // Already open

    const positionStrategy = this.overlay.position()
      .global()
      .centerHorizontally()
      .centerVertically();

    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'kpi-fullscreen-backdrop',
      panelClass: 'kpi-fullscreen-overlay-panel',
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: '100vw',
      height: '100vh',
    });

    // Close on backdrop click
    this.overlayRef.backdropClick().subscribe(() => this.closeFullscreen());

    // Close on Escape key
    this.overlayRef.keydownEvents().subscribe((event: KeyboardEvent) => {
      if (event.key === 'Escape') this.closeFullscreen();
    });

    this.isFullscreen = true;
    this.cdr.detectChanges();

    const portal = new TemplatePortal(this.fullscreenTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);

    // Query the container from the overlay DOM after it's attached
    setTimeout(() => {
      this.fullscreenSvgContainerEl = this.overlayRef?.overlayElement?.querySelector(
        '.fullscreen-chart-container'
      ) as HTMLDivElement;
      this.renderFullscreenVisualization();

      // Re-render fullscreen chart on window resize
      this.fullscreenResizeSub = fromEvent(window, 'resize')
        .pipe(debounceTime(150))
        .subscribe(() => {
          if (!this.fullscreenSvgContainerEl) return;
          const existing = this.fullscreenSvgContainerEl.querySelector('svg');
          if (existing) existing.remove();
          this.renderFullscreenVisualization();
        });
    }, 50);
  }

  /**
   * Close fullscreen overlay
   */
  closeFullscreen(): void {
    this.fullscreenSvgContainerEl = null;
    if (this.fullscreenResizeSub) {
      this.fullscreenResizeSub.unsubscribe();
      this.fullscreenResizeSub = null;
    }
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.isFullscreen = false;
    this.cdr.markForCheck();
  }

  /**
   * Render visualization in fullscreen mode with larger dimensions
   */
  private renderFullscreenVisualization(): void {
    if (!this.fullscreenSvgContainerEl) return;
    
    const { type } = this.kpiConfig.visualization;

    switch (type) {
      case 'sparkline':
        this.renderFullscreenSparkline();
        break;
      case 'bar':
        this.renderFullscreenBarChart();
        break;
      case 'highlighted-bar':
        this.renderFullscreenHighlightedBarChart();
        break;
      case 'category-bar':
        this.renderFullscreenCategoryBarChart();
        break;
    }
  }

  /**
   * Render fullscreen sparkline chart with D3-style tooltip
   */
  private renderFullscreenSparkline(): void {
    const viz = this.kpiConfig.visualization as Extract<KpiVisualization, { type: 'sparkline' }>;
    const data = viz.data;

    if (!data || data.length === 0) return;

    const { w: fullscreenW, h: fullscreenH } = this.getFullscreenChartSize();
    const padding = this.FULLSCREEN_SPARKLINE_PADDING;
    const width = fullscreenW - padding.left - padding.right;
    const height = fullscreenH - padding.top - padding.bottom;

    const minValue = Math.min(...data);
    const maxValue = Math.max(...data);
    const range = maxValue - minValue || 1;

    const svg = this.createFullscreenSvg();
    this.clearSvg(svg);

    this.createDropShadowFilter(svg, 'fullscreenSparklineGlow');

    // Add axes and get nice scale for consistent point positioning
    const { niceMin, niceMax } = this.addFullscreenAxes(svg, padding, width, height, minValue, maxValue, data.length, viz.labels, fullscreenH);
    const niceRange = (niceMax - niceMin) || 1;

    const points = data.map((d, i) => {
      const x = padding.left + (width / (data.length - 1)) * i;
      const y = padding.top + height - ((d - niceMin) / niceRange) * height;
      return { x, y, value: d, index: i };
    });

    const pathData = this.generateCatmullRomPath(points);

    // Vertical gradient: opaque blue at top → fully transparent at bottom
    const defs = svg.querySelector('defs') || (() => {
      const d = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
      svg.insertBefore(d, svg.firstChild);
      return d;
    })();
    const areaGradientId = 'fullscreenAreaGradient';
    let areaGradient = defs.querySelector(`#${areaGradientId}`);
    if (!areaGradient) {
      areaGradient = document.createElementNS('http://www.w3.org/2000/svg', 'linearGradient');
      areaGradient.setAttribute('id', areaGradientId);
      areaGradient.setAttribute('x1', '0%'); areaGradient.setAttribute('y1', '0%');
      areaGradient.setAttribute('x2', '0%'); areaGradient.setAttribute('y2', '100%');
      const s1 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
      s1.setAttribute('offset', '0%');   s1.setAttribute('stop-color', '#2979FF'); s1.setAttribute('stop-opacity', '0.25');
      const s2 = document.createElementNS('http://www.w3.org/2000/svg', 'stop');
      s2.setAttribute('offset', '100%'); s2.setAttribute('stop-color', '#2979FF'); s2.setAttribute('stop-opacity', '0');
      areaGradient.appendChild(s1); areaGradient.appendChild(s2);
      defs.appendChild(areaGradient);
    }

    const areaPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    const closingPath = `${pathData} L ${padding.left + width} ${padding.top + height} L ${padding.left} ${padding.top + height} Z`;
    areaPath.setAttribute('d', closingPath);
    areaPath.setAttribute('fill', `url(#${areaGradientId})`);
    svg.appendChild(areaPath);

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', pathData);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', '#2979FF');
    path.setAttribute('stroke-width', '4');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('filter', 'url(#fullscreenSparklineGlow)');
    svg.appendChild(path);

    // Add small dots at each data point for visual anchoring
    points.forEach(pt => {
      const dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      dot.setAttribute('cx', String(pt.x));
      dot.setAttribute('cy', String(pt.y));
      dot.setAttribute('r', '4');
      dot.setAttribute('fill', '#2979FF');
      dot.setAttribute('stroke', '#ffffff');
      dot.setAttribute('stroke-width', '2');
      dot.style.pointerEvents = 'none';
      svg.appendChild(dot);
    });

    // Add value labels on top of each data point
    points.forEach((pt, i) => {
      const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      const isFirst = i === 0;
      const isLast = i === points.length - 1;
      label.setAttribute('x', String(pt.x + (isFirst ? 12 : isLast ? -12 : 0)));
      label.setAttribute('y', String(pt.y - 12));
      label.setAttribute('text-anchor', isFirst ? 'start' : isLast ? 'end' : 'middle');
      label.setAttribute('font-size', '11');
      label.setAttribute('font-weight', '600');
      label.setAttribute('font-family', "'Poppins'");
      label.setAttribute('fill', '#333');
      label.style.pointerEvents = 'none';
      const displayVal = Number.isInteger(pt.value) ? String(pt.value) : pt.value.toFixed(1);
      label.textContent = displayVal + (this.kpiConfig?.chart_unit || '');
      svg.appendChild(label);
    });

    // Add D3-style interactive tooltip overlay
    this.addSparklineD3Tooltip(svg, points, padding, width, height, minValue, maxValue, fullscreenW, viz.labels);
  }

  /**
   * Render fullscreen bar chart
   */
  private renderFullscreenBarChart(): void {
    const viz = this.kpiConfig.visualization as Extract<KpiVisualization, { type: 'bar' }>;
    const data = viz.data;

    if (!data || data.length === 0) return;

    this.renderFullscreenBars(data, -1);
  }

  /**
   * Render fullscreen highlighted bar chart with axes, hover, and tooltips
   */
  private renderFullscreenHighlightedBarChart(): void {
    const viz = this.kpiConfig.visualization as Extract<KpiVisualization, { type: 'highlighted-bar' }>;
    const data = viz.data;
    const isPrimary = viz.isPrimary || [];
    const labels = viz.labels || data.map((_, i) => `Item ${i + 1}`);

    if (!data || data.length === 0) return;

    const { w: fullscreenW, h: fullscreenH } = this.getFullscreenChartSize();
    const padding = this.FULLSCREEN_BAR_PADDING;
    const width = fullscreenW - padding.left - padding.right;
    const height = fullscreenH - padding.top - padding.bottom;

    const maxValue = Math.max(...data, 1);
    const { barWidth, gap, offsetX } = this.getFullscreenBarLayout(width, data.length);

    const svg = this.createFullscreenSvg();
    this.clearSvg(svg);

    // Add axes and get niceMax for consistent scaling
    const niceMax = this.addFullscreenBarAxes(svg, padding, width, height, maxValue, labels, fullscreenH, barWidth, gap, offsetX);
    const scaleMax = niceMax || maxValue;

    const guideLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    guideLine.setAttribute('stroke', '#2979FF');
    guideLine.setAttribute('stroke-width', '2');
    guideLine.setAttribute('stroke-dasharray', '6,4');
    guideLine.setAttribute('opacity', '0');
    guideLine.style.transition = 'opacity 0.05s ease';
    svg.appendChild(guideLine);

    data.forEach((value, index) => {
      const barHeight = (value / scaleMax) * height;
      const x = padding.left + offsetX + index * (barWidth + gap);
      const y = padding.top + height - barHeight;
      const barCenterX = x + barWidth / 2;

      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(x));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(barWidth));
      rect.setAttribute('height', String(barHeight));

      const defaultColor = isPrimary[index] ? '#2979FF' : '#CFD8DC';
      rect.setAttribute('fill', defaultColor);
      rect.setAttribute('rx', '6');
      rect.setAttribute('ry', '6');
      rect.style.cursor = 'pointer';
      rect.style.transition = 'fill 0.2s ease';

      const hoverRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      hoverRect.setAttribute('x', String(x - Math.max(2, gap / 2)));
      hoverRect.setAttribute('y', String(padding.top));
      hoverRect.setAttribute('width', String(barWidth + gap));
      hoverRect.setAttribute('height', String(height));
      hoverRect.setAttribute('fill', 'transparent');
      hoverRect.style.cursor = 'pointer';

      // Value label on top of bar (always visible on load)
      const valueLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      valueLabel.setAttribute('x', String(barCenterX));
      valueLabel.setAttribute('y', String(y - 8));
      valueLabel.setAttribute('text-anchor', 'middle');
      valueLabel.setAttribute('font-size', '13');
      valueLabel.setAttribute('font-weight', '600');
      valueLabel.setAttribute('font-family', "Poppins");
      valueLabel.setAttribute('fill', '#374151');
      valueLabel.setAttribute('opacity', '1');
      valueLabel.style.pointerEvents = 'none';
      valueLabel.textContent = String(value) + (this.kpiConfig?.chart_unit || '');
      svg.appendChild(valueLabel);

      hoverRect.addEventListener('mouseenter', () => {
        rect.setAttribute('fill', isPrimary[index] ? '#1565C0' : '#2979FF');

        guideLine.setAttribute('x1', String(barCenterX));
        guideLine.setAttribute('y1', String(padding.top));
        guideLine.setAttribute('x2', String(barCenterX));
        guideLine.setAttribute('y2', String(padding.top + height));
        guideLine.setAttribute('opacity', '1');

        const tooltipDiv = this.getOrCreateD3TooltipContainer();
        const containerRect = this.fullscreenSvgContainerEl?.getBoundingClientRect();
        if (containerRect) {
          const yScale = containerRect.height / fullscreenH;
          const xScale = containerRect.width / fullscreenW;
          const tooltipX = containerRect.left + (barCenterX * xScale) + 15;
          const tooltipY = containerRect.top + (y * yScale) - 80;
          this.showUnifiedTooltip(tooltipDiv, tooltipX, tooltipY, labels[index], value, defaultColor);
        }
      });

      hoverRect.addEventListener('mouseleave', () => {
        rect.setAttribute('fill', defaultColor);
        guideLine.setAttribute('opacity', '0');
        const tooltipDiv = this.getOrCreateD3TooltipContainer();
        tooltipDiv.style.opacity = '0';
      });

      svg.appendChild(rect);
      svg.appendChild(hoverRect);
    });
  }

  /**
   * Render fullscreen category bar chart
   */
  private renderFullscreenCategoryBarChart(): void {
    const viz = this.kpiConfig.visualization as Extract<KpiVisualization, { type: 'category-bar' }>;
    const data = viz.data;
    const selectedIndex = viz.selectedIndex ?? 0;

    if (!data || data.length === 0) return;

    this.renderFullscreenBars(data, selectedIndex);
  }

  /**
   * Render fullscreen interactive bars with axes and enhanced tooltips
   */
  private renderFullscreenBars(data: number[], selectedIndex: number = -1): void {
    const { w: fullscreenW, h: fullscreenH } = this.getFullscreenChartSize();
    const padding = this.FULLSCREEN_BAR_PADDING;
    const width = fullscreenW - padding.left - padding.right;
    const height = fullscreenH - padding.top - padding.bottom;

    const maxValue = Math.max(...data, 1);
    const { barWidth, gap, offsetX } = this.getFullscreenBarLayout(width, data.length);

    const svg = this.createFullscreenSvg();
    this.clearSvg(svg);

    const viz = this.kpiConfig.visualization;
    const labels = (viz.type === 'bar' && viz.labels) ? viz.labels :
                   (viz.type === 'highlighted-bar' && viz.labels) ? viz.labels :
                   (viz.type === 'category-bar' && viz.labels) ? viz.labels :
                   data.map((_, i) => `Item ${i + 1}`);

    // Add axes and get niceMax for consistent scaling
    const niceMax = this.addFullscreenBarAxes(svg, padding, width, height, maxValue, labels, fullscreenH, barWidth, gap, offsetX);
    const scaleMax = niceMax || maxValue;

    const tooltip = this.getOrCreateFullscreenTooltip();
    tooltip.style.opacity = '0';
    tooltip.style.pointerEvents = 'none';

    const guideLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    guideLine.setAttribute('stroke', '#2979FF');
    guideLine.setAttribute('stroke-width', '2');
    guideLine.setAttribute('stroke-dasharray', '6,4');
    guideLine.setAttribute('opacity', '0');
    guideLine.style.transition = 'opacity 0.05s ease';
    svg.appendChild(guideLine);

    data.forEach((value, index) => {
      const barHeight = (value / scaleMax) * height;
      const x = padding.left + offsetX + index * (barWidth + gap);
      const y = padding.top + height - barHeight;
      const barCenterX = x + barWidth / 2;

      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(x));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(barWidth));
      rect.setAttribute('height', String(barHeight));
      rect.setAttribute('rx', '6');
      rect.setAttribute('ry', '6');
      rect.style.cursor = 'pointer';
      rect.style.transition = 'fill 0.2s ease';

      let defaultColor = '#CFD8DC';
      if (selectedIndex >= 0 && index === selectedIndex) {
        defaultColor = '#2979FF';
      }
      rect.setAttribute('fill', defaultColor);

      const hoverRect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      hoverRect.setAttribute('x', String(x - Math.max(2, gap / 2)));
      hoverRect.setAttribute('y', String(padding.top));
      hoverRect.setAttribute('width', String(barWidth + gap));
      hoverRect.setAttribute('height', String(height));
      hoverRect.setAttribute('fill', 'transparent');
      hoverRect.style.cursor = 'pointer';

      // Value label on top of bar (always visible on load)
      const valueLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      valueLabel.setAttribute('x', String(barCenterX));
      valueLabel.setAttribute('y', String(y - 8));
      valueLabel.setAttribute('text-anchor', 'middle');
      valueLabel.setAttribute('font-size', '13');
      valueLabel.setAttribute('font-weight', '600');
      valueLabel.setAttribute('font-family', "Poppins");
      valueLabel.setAttribute('fill', '#374151');
      valueLabel.setAttribute('opacity', '1');
      valueLabel.style.pointerEvents = 'none';
      valueLabel.textContent = String(value) + (this.kpiConfig?.chart_unit || '');
      svg.appendChild(valueLabel);

      hoverRect.addEventListener('mouseenter', () => {
        rect.setAttribute('fill', '#2979FF');

        guideLine.setAttribute('x1', String(barCenterX));
        guideLine.setAttribute('y1', String(padding.top));
        guideLine.setAttribute('x2', String(barCenterX));
        guideLine.setAttribute('y2', String(padding.top + height));
        guideLine.setAttribute('opacity', '1');

        // Show D3-style tooltip with improved positioning
        const tooltipDiv = this.getOrCreateD3TooltipContainer();
        const containerRect = this.fullscreenSvgContainerEl?.getBoundingClientRect();
        if (containerRect) {
          const yScale = containerRect.height / fullscreenH;
          const xScale = containerRect.width / fullscreenW;
          const tooltipX = containerRect.left + (barCenterX * xScale) + 15;
          const tooltipY = containerRect.top + (y * yScale) - 80;

          this.showUnifiedTooltip(tooltipDiv, tooltipX, tooltipY, labels[index], value, '#2979FF');
        }
      });

      hoverRect.addEventListener('mouseleave', () => {
        rect.setAttribute('fill', defaultColor);
        guideLine.setAttribute('opacity', '0');
        const tooltipDiv = this.getOrCreateD3TooltipContainer();
        tooltipDiv.style.opacity = '0';
      });

      svg.appendChild(rect);
      svg.appendChild(hoverRect);
    });
  }

  /**
   * Create fullscreen SVG element
   */
  private createFullscreenSvg(): SVGSVGElement {
    const { w: fullscreenW, h: fullscreenH } = this.getFullscreenChartSize();
    let svg = this.fullscreenSvgContainerEl?.querySelector('svg');

    if (!svg) {
      svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      svg.setAttribute('width', '100%');
      svg.setAttribute('height', '100%');

      if (this.fullscreenSvgContainerEl) {
        this.fullscreenSvgContainerEl.appendChild(svg);
      }
    }

    svg.setAttribute('viewBox', `0 0 ${fullscreenW} ${fullscreenH}`);

    return svg;
  }

  /**
   * Get or create fullscreen tooltip element
   */
  private getOrCreateFullscreenTooltip(): HTMLElement {
    const tooltipArea = this.fullscreenSvgContainerEl?.parentElement?.querySelector('.fullscreen-tooltip-area');
    let tooltip = tooltipArea?.querySelector('.fullscreen-chart-tooltip') as HTMLElement;
    
    if (!tooltip) {
      tooltip = this.fullscreenSvgContainerEl?.querySelector('.fullscreen-chart-tooltip') as HTMLElement;
      if (!tooltip) {
        tooltip = document.createElement('div');
        tooltip.className = 'fullscreen-chart-tooltip';
        this.fullscreenSvgContainerEl?.appendChild(tooltip);
      }
    }
    return tooltip;
  }

  /**
   * Show fullscreen tooltip
   */
  private showFullscreenTooltip(tooltip: HTMLElement, label: string, barCenterX: number): void {
    tooltip.textContent = label;
    
    const containerRect = this.fullscreenSvgContainerEl?.getBoundingClientRect();
    if (!containerRect) return;

    const { w: fullscreenW } = this.getFullscreenChartSize();
    const xScale = containerRect.width / fullscreenW;
    const tooltipX = barCenterX * xScale;

    tooltip.style.left = `${tooltipX}px`;
    tooltip.style.opacity = '1';
  }

  /**
   * Hide fullscreen tooltip
   */
  private hideFullscreenTooltip(tooltip: HTMLElement): void {
    tooltip.style.opacity = '0';
  }

  /**
   * Add axes for fullscreen sparkline
   */
  private addFullscreenAxes(svg: SVGSVGElement, padding: any, width: number, height: number, minValue: number, maxValue: number, dataLength: number, labels: string[] | undefined, totalHeight: number): { niceMin: number; niceMax: number } {
    const fontFamily = "poppins";

    // ── Nice Y-axis scale ──────────────────────────────────────────────────
    // Compute a rounded tick interval so labels are clean integers like 180, 200, 220...
    const ySteps = 5;
    const rawStep = (maxValue - minValue) / ySteps;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const niceMultipliers = [1, 2, 2.5, 5, 10];
    let niceStep = magnitude;
    for (const m of niceMultipliers) {
      const candidate = magnitude * m;
      if (candidate >= rawStep) { niceStep = candidate; break; }
    }
    const niceMin = Math.floor(minValue / niceStep) * niceStep;
    const niceMax = Math.ceil(maxValue / niceStep) * niceStep;
    const niceStepCount = Math.round((niceMax - niceMin) / niceStep);

    // ── Horizontal grid lines + Y-axis tick labels ─────────────────────────
    // No Y-axis line, no X-axis line — only subtle horizontal grid lines
    for (let i = 0; i <= niceStepCount; i++) {
      const value = niceMin + niceStep * i;
      const y = padding.top + height - ((value - niceMin) / (niceMax - niceMin)) * height;

      // Skip lines outside the visible area
      if (y < padding.top - 1 || y > padding.top + height + 1) continue;

      // Subtle gray grid line (no dashes)
      const gridLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      gridLine.setAttribute('x1', String(padding.left));
      gridLine.setAttribute('y1', String(y));
      gridLine.setAttribute('x2', String(padding.left + width));
      gridLine.setAttribute('y2', String(y));
      gridLine.setAttribute('stroke', '#e5e7eb');
      gridLine.setAttribute('stroke-width', '1');
      svg.appendChild(gridLine);

      // Tick label: right-aligned, humanized number
      const unit = this.kpiConfig?.chart_unit || '';
      const displayVal = this.humanizeYValue(value) + unit;
      const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      label.setAttribute('x', String(padding.left - 8));
      label.setAttribute('y', String(y + 4));
      label.setAttribute('text-anchor', 'end');
      label.setAttribute('font-size', '11');
      label.setAttribute('font-weight', '400');
      label.setAttribute('font-family', fontFamily);
      label.setAttribute('fill', '#6b7280');
      label.textContent = displayVal;
      svg.appendChild(label);
    }

    // ── X-axis labels ──────────────────────────────────────────────────────
    const xSteps = Math.min(dataLength - 1, 8);
    const stepSize = Math.floor(dataLength / xSteps) || 1;
    for (let i = 0; i < dataLength; i += stepSize) {
      const x = padding.left + (width * i / (dataLength - 1));
      const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      label.setAttribute('x', String(x));
      label.setAttribute('y', String(padding.top + height + 22));
      label.setAttribute('text-anchor', 'middle');
      label.setAttribute('font-size', '11');
      label.setAttribute('font-weight', '400');
      label.setAttribute('font-family', fontFamily);
      label.setAttribute('fill', '#6b7280');
      // Use provided label string if available, otherwise fall back to index number
      label.textContent = (labels && labels[i]) ? labels[i] : String(i + 1);
      svg.appendChild(label);
    }

    // ── Axis titles (light weight, won't overpower) ────────────────────────
    const yAxisTitle = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    yAxisTitle.setAttribute('x', String(-(padding.top + height / 2)));
    yAxisTitle.setAttribute('y', '14');
    yAxisTitle.setAttribute('text-anchor', 'middle');
    yAxisTitle.setAttribute('font-size', '12');
    yAxisTitle.setAttribute('font-weight', '500');
    yAxisTitle.setAttribute('font-family', fontFamily);
    yAxisTitle.setAttribute('fill', '#101d42');
    yAxisTitle.setAttribute('transform', 'rotate(-90)');
    yAxisTitle.textContent = this.kpiConfig?.yAxisLabel || 'Value';
    svg.appendChild(yAxisTitle);

    const xAxisTitle = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    xAxisTitle.setAttribute('x', String(padding.left + width / 2));
    xAxisTitle.setAttribute('y', String(totalHeight - 8));
    xAxisTitle.setAttribute('text-anchor', 'middle');
    xAxisTitle.setAttribute('font-size', '12');
    xAxisTitle.setAttribute('font-weight', '500');
    xAxisTitle.setAttribute('font-family', fontFamily);
    xAxisTitle.setAttribute('fill', '#101d42');
    xAxisTitle.textContent = this.kpiConfig?.xAxisLabel || 'Period';
    svg.appendChild(xAxisTitle);

    return { niceMin, niceMax };
  }

  /**
   * Add axes for fullscreen bar charts
   */
  private addFullscreenBarAxes(svg: SVGSVGElement, padding: any, width: number, height: number, maxValue: number, labels: string[], totalHeight: number, barWidth: number, gap: number, offsetX: number = 0): number {
    const fontFamily = "poppins";

    // ── Y-axis: nice rounded ticks ─────────────────────────────────────────
    const ySteps = 5;
    const rawStep = maxValue / ySteps;
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep || 1)));
    const niceMultipliers = [1, 2, 2.5, 5, 10];
    let niceStep = magnitude;
    for (const m of niceMultipliers) {
      const candidate = magnitude * m;
      if (candidate >= rawStep) { niceStep = candidate; break; }
    }
    const niceMax = Math.ceil(maxValue / niceStep) * niceStep;
    const niceStepCount = Math.round(niceMax / niceStep);

    // ── Horizontal grid lines + Y-axis tick labels ─────────────────────────
    for (let i = 0; i <= niceStepCount; i++) {
      const value = niceStep * i;
      const y = padding.top + height - (value / niceMax) * height;

      if (y < padding.top - 1 || y > padding.top + height + 1) continue;

      const gridLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      gridLine.setAttribute('x1', String(padding.left));
      gridLine.setAttribute('y1', String(y));
      gridLine.setAttribute('x2', String(padding.left + width));
      gridLine.setAttribute('y2', String(y));
      gridLine.setAttribute('stroke', '#e5e7eb');
      gridLine.setAttribute('stroke-width', '1');
      svg.appendChild(gridLine);

      const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      label.setAttribute('x', String(padding.left - 8));
      label.setAttribute('y', String(y + 4));
      label.setAttribute('text-anchor', 'end');
      label.setAttribute('font-size', '11');
      label.setAttribute('font-weight', '400');
      label.setAttribute('font-family', fontFamily);
      label.setAttribute('fill', '#6b7280');
      label.textContent = this.humanizeYValue(value) + (this.kpiConfig?.chart_unit  || '');
      svg.appendChild(label);
    }

    // ── X-axis labels ──────────────────────────────────────────────────────
    labels.forEach((labelText, index) => {
      const x = padding.left + offsetX + index * (barWidth + gap) + barWidth / 2;

      const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      label.setAttribute('x', String(x));
      label.setAttribute('y', String(padding.top + height + 22));
      label.setAttribute('text-anchor', 'middle');
      label.setAttribute('font-size', '11');
      label.setAttribute('font-weight', '400');
      label.setAttribute('font-family', fontFamily);
      label.setAttribute('fill', '#6b7280');
      label.textContent = labelText.length > 10 ? labelText.substring(0, 8) + '...' : labelText;
      svg.appendChild(label);
    });

    // ── Axis titles ────────────────────────────────────────────────────────
    const yAxisTitle = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    yAxisTitle.setAttribute('x', String(-(padding.top + height / 2)));
    yAxisTitle.setAttribute('y', '14');
    yAxisTitle.setAttribute('text-anchor', 'middle');
    yAxisTitle.setAttribute('font-size', '12');
    yAxisTitle.setAttribute('font-weight', '500');
    yAxisTitle.setAttribute('font-family', fontFamily);
    yAxisTitle.setAttribute('fill', '#101d42');
    yAxisTitle.setAttribute('transform', 'rotate(-90)');
    yAxisTitle.textContent = this.kpiConfig?.yAxisLabel || 'Value';
    svg.appendChild(yAxisTitle);

    const xAxisTitle = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    xAxisTitle.setAttribute('x', String(padding.left + width / 2));
    xAxisTitle.setAttribute('y', String(totalHeight - 8));
    xAxisTitle.setAttribute('text-anchor', 'middle');
    xAxisTitle.setAttribute('font-size', '12');
    xAxisTitle.setAttribute('font-weight', '400');
    xAxisTitle.setAttribute('font-family', fontFamily);
    xAxisTitle.setAttribute('fill', '#101d42');
    xAxisTitle.textContent = this.kpiConfig?.xAxisLabel || 'Period';
    svg.appendChild(xAxisTitle);

    return niceMax;
  }

  /** Format large Y-axis values: 1000 → 1K, 1000000 → 1M, etc. */
  private humanizeYValue(value: number): string {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return (value / 1_000_000).toFixed(abs % 1_000_000 === 0 ? 0 : 1) + 'M';
    if (abs >= 1_000)     return (value / 1_000).toFixed(abs % 1_000 === 0 ? 0 : 1) + 'K';
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }

  /**
   * Add interactive data points to sparkline
   */
  private addSparklineDataPoints(svg: SVGSVGElement, points: Array<{x: number, y: number, value: number}>, minValue: number, maxValue: number, range: number): void {
    points.forEach((point, index) => {
      // Create circle for data point
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', String(point.x));
      circle.setAttribute('cy', String(point.y));
      circle.setAttribute('r', '5');
      circle.setAttribute('fill', '#2979FF');
      circle.setAttribute('stroke', '#ffffff');
      circle.setAttribute('stroke-width', '2');
      circle.style.cursor = 'pointer';
      circle.style.transition = 'r 0.2s ease';

      // Tooltip on hover
      const tooltip = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      tooltip.setAttribute('x', String(point.x));
      tooltip.setAttribute('y', String(point.y - 15));
      tooltip.setAttribute('text-anchor', 'middle');
      tooltip.setAttribute('font-size', '12');
      tooltip.setAttribute('font-weight', '700');
      tooltip.setAttribute('fill', '#1f2937');
      tooltip.setAttribute('opacity', '0');
      tooltip.style.pointerEvents = 'none';
      tooltip.textContent = Number.isInteger(point.value) ? String(point.value) : point.value.toFixed(1);
      svg.appendChild(tooltip);

      circle.addEventListener('mouseenter', () => {
        circle.setAttribute('r', '7');
        tooltip.setAttribute('opacity', '1');
      });

      circle.addEventListener('mouseleave', () => {
        circle.setAttribute('r', '5');
        tooltip.setAttribute('opacity', '0');
      });

      svg.appendChild(circle);
    });
  }

  private getUnifiedTooltipHtml(label: string, value: string | number, dotColor: string = '#2979FF'): string {
    return `
      <div style="display:flex;align-items:center;gap:6px;font-size:12px;font-family:Poppins;">
        <span style="width:8px;height:8px;border-radius:50%;flex-shrink:0;background-color:${dotColor};"></span>
        <span style="color:#6b7280;font-weight:500;">${label}</span>
        <span style="font-weight:700;color:#1f2937;font-size:13px;">${value}</span>
      </div>`;
  }

  private showUnifiedTooltip(tooltipDiv: HTMLElement, x: number, y: number, label: string, value: string | number, dotColor: string = '#2979FF'): void {
    const unit = this.kpiConfig?.chart_unit || '';
    const displayValue = unit ? `${value}${unit}` : value;
    tooltipDiv.innerHTML = this.getUnifiedTooltipHtml(label, displayValue, dotColor);
    tooltipDiv.style.display = 'block';
    tooltipDiv.style.visibility = 'visible';
    tooltipDiv.style.opacity = '1';

    // Dynamically clamp tooltip position within viewport bounds
    const tooltipRect = tooltipDiv.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;

    // If tooltip overflows right edge, flip to the left of the cursor
    if (x + tooltipRect.width > vw - margin) {
      x = x - tooltipRect.width - 30;
    }
    // If tooltip overflows left edge, pin to left margin
    if (x < margin) {
      x = margin;
    }
    // If tooltip overflows top edge, flip below the cursor
    if (y < margin) {
      y = margin;
    }
    // If tooltip overflows bottom edge, shift up
    if (y + tooltipRect.height > vh - margin) {
      y = vh - margin - tooltipRect.height;
    }

    tooltipDiv.style.left = `${x}px`;
    tooltipDiv.style.top = `${y}px`;
  }

  /**
   * Add D3-style interactive tooltip for sparkline with crosshair
   */
  private addSparklineD3Tooltip(svg: SVGSVGElement, points: Array<{x: number, y: number, value: number, index: number}>, padding: any, width: number, height: number, minValue: number, maxValue: number, chartWidth: number = this.FULLSCREEN_CHART_WIDTH, labels?: string[]): void {
    // Create vertical crosshair line
    const crosshair = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    crosshair.setAttribute('stroke', '#6366f1');
    crosshair.setAttribute('stroke-width', '2');
    crosshair.setAttribute('stroke-dasharray', '5,5');
    crosshair.setAttribute('opacity', '0');
    crosshair.setAttribute('y1', String(padding.top));
    crosshair.setAttribute('y2', String(padding.top + height));
    crosshair.style.pointerEvents = 'none';
    svg.appendChild(crosshair);

    // Create hover circle
    const hoverCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    hoverCircle.setAttribute('r', '6');
    hoverCircle.setAttribute('fill', '#2979FF');
    hoverCircle.setAttribute('stroke', '#ffffff');
    hoverCircle.setAttribute('stroke-width', '3');
    hoverCircle.setAttribute('opacity', '0');
    hoverCircle.style.pointerEvents = 'none';
    svg.appendChild(hoverCircle);

    // Create tooltip container in DOM
    const tooltipDiv = this.createD3TooltipContainer();

    // Create transparent overlay for mouse tracking
    const overlay = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    overlay.setAttribute('x', String(padding.left));
    overlay.setAttribute('y', String(padding.top));
    overlay.setAttribute('width', String(width));
    overlay.setAttribute('height', String(height));
    overlay.setAttribute('fill', 'transparent');
    overlay.style.cursor = 'crosshair';

    overlay.addEventListener('mousemove', (event: MouseEvent) => {
      const svgRect = svg.getBoundingClientRect();
      const mouseX = event.clientX - svgRect.left;
      const mouseXScaled = mouseX * (chartWidth / svgRect.width);

      // Find closest point
      let closestPoint = points[0];
      let minDistance = Math.abs(mouseXScaled - closestPoint.x);

      points.forEach(point => {
        const distance = Math.abs(mouseXScaled - point.x);
        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = point;
        }
      });

      // Update crosshair position
      crosshair.setAttribute('x1', String(closestPoint.x));
      crosshair.setAttribute('x2', String(closestPoint.x));
      crosshair.setAttribute('opacity', '1');

      // Update hover circle
      hoverCircle.setAttribute('cx', String(closestPoint.x));
      hoverCircle.setAttribute('cy', String(closestPoint.y));
      hoverCircle.setAttribute('opacity', '1');

      // Update tooltip with better positioning
      const tooltipX = event.clientX + 15;
      const tooltipY = event.clientY - 80;

      this.showUnifiedTooltip(
        tooltipDiv,
        tooltipX,
        tooltipY,
        labels?.[closestPoint.index] ?? `Point ${closestPoint.index + 1}`,
        Number.isInteger(closestPoint.value) ? String(closestPoint.value) : closestPoint.value.toFixed(1),
        '#2979FF'
      );
    });

    overlay.addEventListener('mouseleave', () => {
      crosshair.setAttribute('opacity', '0');
      hoverCircle.setAttribute('opacity', '0');
      tooltipDiv.style.opacity = '0';
    });

    svg.appendChild(overlay);
  }

  /**
   * Create D3-style tooltip container
   * Styles are applied inline because the element lives on document.body,
   * outside Angular's component scope where scoped SCSS cannot reach.
   */
  private createD3TooltipContainer(): HTMLElement {
    let tooltip = document.querySelector('.d3-chart-tooltip') as HTMLElement;

    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.className = 'd3-chart-tooltip';

      // Layout & positioning
      Object.assign(tooltip.style, {
        position:        'fixed',
        zIndex:          '999999',
        pointerEvents:   'none',
        display:         'block',
        opacity:         '0',
        transition:      'opacity 0.1s ease',
        willChange:      'opacity, left, top',

        // Sizing
        minWidth:        'auto',
        maxWidth:        '260px',
        padding:         '6px 12px',

        // Appearance
        background:      '#ffffff',
        color:           '#1f2937',
        borderRadius:    '6px',
        border:          '1px solid #e5e7eb',
        boxShadow:       '0 2px 8px rgba(0,0,0,0.12)',

        // Typography
        fontFamily:      'Poppins',
        fontSize:        '12px',
        lineHeight:      '1.4',
      });

      // Hide tooltip immediately on any scroll anywhere in the page.
      // capture:true catches scroll on nested scrollable containers too.
      if (!this.scrollHideHandler) {
        this.scrollHideHandler = () => {
          const t = document.querySelector('.d3-chart-tooltip') as HTMLElement | null;
          if (t) t.style.opacity = '0';
        };
        window.addEventListener('scroll', this.scrollHideHandler, { capture: true, passive: true });
      }

      // Inject inner CSS for child elements via a <style> tag (once only)
      if (!document.querySelector('#kpi-d3-tooltip-styles')) {
        const style = document.createElement('style');
        style.id = 'kpi-d3-tooltip-styles';
        style.textContent = `
          .d3-chart-tooltip .d3-tooltip-header {
            font-size: 12px;
            font-weight: 600;
            color: #e2e8f0;
            margin-bottom: 8px;
            padding-bottom: 6px;
            border-bottom: 1px solid rgba(255,255,255,0.15);
          }
          .d3-chart-tooltip .d3-tooltip-body {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          .d3-chart-tooltip .d3-tooltip-row {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 2px 0;
          }
          .d3-chart-tooltip .d3-tooltip-dot {
            width: 9px;
            height: 9px;
            border-radius: 50%;
            flex-shrink: 0;
          }
          .d3-chart-tooltip .d3-tooltip-label {
            flex: 1;
            color: #cbd5e0;
            font-size: 12px;
            font-weight: 500;
          }
          .d3-chart-tooltip .d3-tooltip-value {
            font-weight: 700;
            color: #ffffff;
            font-size: 13px;
          }
        `;
        document.head.appendChild(style);
      }

      document.body.appendChild(tooltip);
    }

    return tooltip;
  }

  /**
   * Get or create D3-style tooltip container
   */
  private getOrCreateD3TooltipContainer(): HTMLElement {
    return this.createD3TooltipContainer();
  }

    closeMenu() {
    this.trigger.closeMenu();
  }

  displayMainValue(value: any): string {
    if (typeof value === 'string') return value;
    if (value == null) return '0';
    const isHmMode = typeof this.kpiConfig?.mainValue === 'string' &&
      /^\d+h \d+m$|^\d+m$/.test(String(this.kpiConfig.mainValue).trim());
    if (isHmMode) {
      const h = Math.floor(value / 60);
      const m = Math.floor(value % 60);
      return h > 0 ? `${h}h ${m}m` : `${m}m`;
    }
    return this.formatNumber(Number(value));
  }

  private formatNumber(value: number): string {
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
 
}

